import React, { useState, useEffect } from "react";
import "./ChatMessage.css";
import { translations } from "../../utils/translations";
import { marked } from "marked";
import ChartRenderer from "../ChartRenderer/ChartRenderer";

// Configurar marked para que sea seguro y maneje saltos de línea simples
marked.setOptions({
  breaks: true,
  gfm: true
});

const formatTextSafely = (text, chatTranslations = null) => {
  if (!text) return "";

  const viewLinkText = chatTranslations?.viewLink || "Ver enlace";

  let processedText = "";

  // 1. Manejo Recursivo de Arrays y Objetos complejos
  // Si 'text' es un Array (ej: la respuesta del bot en tu captura)
  if (Array.isArray(text)) {
    // Buscamos el primer elemento que parezca tener contenido
    const validPart = text.find(
      (t) =>
        typeof t === "string" ||
        (typeof t === "object" && (t.text || t.content || t.message))
    );

    if (validPart) {
      // Llamamos recursivamente para extraer el texto de ese elemento
      return formatTextSafely(validPart);
    }
    return ""; // Si es un array vacío o sin texto
  }

  // Si 'text' es un Objeto
  if (typeof text === "object") {
    // Intentamos extraer la propiedad de texto
    const extractedContent = text.text || text.content || text.message;

    // Si encontramos contenido y ese contenido NO es el mismo objeto (para evitar loops)
    if (extractedContent && extractedContent !== text) {
      return formatTextSafely(extractedContent);
    }

    // Si llegamos aquí y sigue siendo objeto, es un caso borde, pero evitamos el JSON gigante
    // A menos que sea estrictamente necesario para depurar, mejor devolver vacío o un fallback
    if (!extractedContent) return "";
    processedText = String(extractedContent);
  } else {
    // Si ya es un string (caso normal)
    processedText = String(text);
  }

  // 1.5 Ocultar bloques JSON y notas de sistema para Canvas
  const instructionRegex = /\[IMPORTANTE:.*?\]/gs;
  processedText = processedText.replace(instructionRegex, '').trim();

  const jsonBlockRegex = /```json[\s\S]*?```/g;
  if (processedText.match(jsonBlockRegex)) {
    processedText = processedText.replace(jsonBlockRegex, '').trim();
  } else {
    const startIndex = processedText.indexOf('{');
    const endIndex = processedText.lastIndexOf('}');
    if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
      const jsonStr = processedText.substring(startIndex, endIndex + 1);
      if (jsonStr.includes('"post":') || jsonStr.includes('"blog":') || jsonStr.includes('"type":"chart"') || jsonStr.includes("'type':'chart'")) {
        processedText = processedText.substring(0, startIndex).trim() + '\n' + processedText.substring(endIndex + 1).trim();
      }
    }
  }

  // --- A partir de aquí tu lógica de formateo original se mantiene igual ---


  // Helper function to get styled link HTML
  const getLinkHtml = (url, type, customText = null) => {
    // Clean URL: Remove backslashes before characters (LLM escape behavior)
    const cleanedUrl = url.replace(/\\/g, "");
    const linkText = customText || viewLinkText;

    return `<a href="${cleanedUrl}" target="_blank" rel="noopener noreferrer" class="chat-link">[ ${linkText} ]</a>`;
  };

  const isCalendarUrl = (url) => /calendar\.google|calendar\.app|calendly\.com|meet\.google|calendar-app|outlook\.office|zoho.*calendar|hubspot.*meetings|cal\.com/i.test(url);
  const isPaymentUrl = (url) => /mercadopago|paypal|stripe|mpago\.la|checkout|payoneer|zelle|venmo|bizum/i.test(url);

  // 2. Convert Markdown-style links
  // Catch [[text](url)] or [text](url) patterns, handle spaces and escaping
  const markdownLinkRegex = /(\[?\s*)\[([^\]]+)\]\s*\\?\(\s*(https?:\/\/[^\s)]+)\s*\\?\)(\s*\]?)/g;
  processedText = processedText.replace(
    markdownLinkRegex,
    (match, prefix, linkText, url, suffix) => {
      // Normalize prefix/suffix to avoid double brackets if they were already in the text like [[link](url)]
      const p = prefix.replace("[", "");
      const s = suffix.replace("]", "");

      if (isCalendarUrl(url)) {
        return `${p}${getLinkHtml(url, "calendar", linkText.trim() === "Ver enlace" || linkText.trim() === "Ver link" || linkText.trim() === "View link" ? null : linkText)}${s}`;
      }
      if (isPaymentUrl(url)) {
        return `${p}${getLinkHtml(url, "payment", linkText.trim() === "Ver enlace" || linkText.trim() === "Ver link" || linkText.trim() === "View link" ? null : linkText)}${s}`;
      }
      return `${p}<a href="${url.replace(/\\/g, "")}" target="_blank" rel="noopener noreferrer" class="chat-link">[ ${linkText} ]</a>${s}`;
    }
  );

  // 3. Convert raw URLs and apply special formatting for Calendar/Payments
  // Added optional bracket capturing to avoid [ [ URL ] ]
  const rawUrlRegex = /(\[?\s*)(\b(https?):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;\\/]*[-A-Z0-9+&@#/%?=~_|])(\s*\]?)/gi;
  processedText = processedText.replace(rawUrlRegex, (match, prefix, url, protocol, suffix) => {
    // Avoid double processing links already in href
    const cleanedUrl = url.replace(/\\/g, "");
    if (processedText.includes(`href="${url}"`) || processedText.includes(`href="${cleanedUrl}"`)) {
      return match;
    }

    const p = prefix.replace("[", "");
    const s = suffix.replace("]", "");

    if (isCalendarUrl(url)) return `${p}${getLinkHtml(url, "calendar")}${s}`;
    if (isPaymentUrl(url)) return `${p}${getLinkHtml(url, "payment")}${s}`;

    return `${p}<a href="${cleanedUrl}" target="_blank" rel="noopener noreferrer" class="chat-link">[ ${url} ]</a>${s}`;
  });

  // 4. Basic formatting - Usamos marked para manejar Markdown completo (tablas, etc)
  processedText = marked.parse(processedText);

  return processedText;
};

const ChatMessage = React.memo(({ sender, text, file, thinking, status }) => {
  const messageClass = `${sender === "user" ? "user-message" : "bot-message"} ${thinking ? "thinking" : ""
    }`;
  const [fileUrl, setFileUrl] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [isAudio, setIsAudio] = useState(false);

  const currentLang = localStorage.getItem("language") || "es";
  const t =
    translations[currentLang]?.chatComponent ||
    translations["es"].chatComponent;

  useEffect(() => {
    if (file) {
      if (file instanceof File) {
        const isImg = file.type.startsWith("image/");
        const isAud = file.type.startsWith("audio/");
        setIsImage(isImg);
        setIsAudio(isAud);
        if (isImg || isAud) {
          const url = URL.createObjectURL(file);
          setFileUrl(url);
          return () => URL.revokeObjectURL(url);
        }
      } else if (file.url && file.mimetype) {
        const isImg = file.mimetype.startsWith("image/");
        const isAud = file.mimetype.startsWith("audio/");
        setIsImage(isImg);
        setIsAudio(isAud);
        if (isImg || isAud) {
          setFileUrl(file.url);
        }
      }
    }
  }, [file]);

  const getFileName = () => {
    if (!file) return t.fileDefaultName;
    return file.name || file.filename || t.fileDefaultName;
  };

  const createMarkup = () => {
    return { __html: formatTextSafely(text, t) };
  };

  const getChartJson = () => {
    if (typeof text !== "string") return null;
    const jsonBlockRegex = /```json([\s\S]*?)```/g;
    const match = jsonBlockRegex.exec(text);
    
    const sanitizeJson = (str) => {
      if (!str) return str;
      return str
        .replace(/True/g, 'true')
        .replace(/False/g, 'false')
        .replace(/None/g, 'null')
        // En algunos casos el agente usa comillas simples para las llaves si viene de un print de dict
        .replace(/'/g, '"');
    };

    if (match && match[1]) {
      const raw = match[1].trim();
      try {
        return JSON.parse(raw);
      } catch(e) {
        try {
          return JSON.parse(sanitizeJson(raw));
        } catch(e2) {}
      }
    }
    // Deep fallback for raw objects
    const startIndex = text.indexOf('{');
    const endIndex = text.lastIndexOf('}');
    if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
      const jsonStr = text.substring(startIndex, endIndex + 1);
      try {
        return JSON.parse(jsonStr);
      } catch(e) {
        try {
          return JSON.parse(sanitizeJson(jsonStr));
        } catch(e2) {}
      }
    }
    return null;
  };

  const chartData = getChartJson();

  if (!file && !text && !thinking) return null;

  return (
    <div className={`chat-message ${messageClass}`}>
      {file && (
        <div className="file-attachment">
          {isImage && fileUrl ? (
            <img src={fileUrl} alt="Adjunto" className="image-thumbnail" />
          ) : isAudio && fileUrl ? (
            <audio controls src={fileUrl} />
          ) : (
            <p>
              <strong>{t.fileAttachedLabel}</strong> {getFileName()}
            </p>
          )}
        </div>
      )}
      {sender === "bot" && status && (
        <div className="stream-status">{status}</div>
      )}
      {text && <div dangerouslySetInnerHTML={createMarkup()} />}
      {chartData && <ChartRenderer jsonStr={chartData} />}
    </div>
  );
});

export { formatTextSafely };
export default ChatMessage;
